import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Pencil, FolderKanban } from 'lucide-react'
import Layout from 'components/Layout'
import Popup from 'components/Popup'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import ProjectModifyPopup from 'components/popups/ProjectModifyPopup'
import LocationActionSheet from 'screens/locations/components/Locations/LocationActionSheet'
import { prepareLocationForSave } from 'screens/locations/utils/prepareLocationForSave'
import { initializeApiKey } from 'slices/apiKeysSlice'
import { initializeLocations, patchLocation, updateLocation } from 'slices/locationSlice'
import { initializeProjects, patchProject } from 'slices/projectSlice'

const SidebarLocationTop = ({ location, project, originator }) => {
	const dispatch = useDispatch()

	const [openPopup, setOpenPopup] = useState(false)
	const [openMap, setOpenMap] = useState(false)
	const [openModifyPopup, setOpenModifyPopup] = useState(false)
	const [recordForEdit, setRecordForEdit] = useState(null)
	const [center, setCenter] = useState([])
	const [mapTitle, setMapTitle] = useState(null)
	const [popupTitle, setPopupTitle] = useState(null)

	const { entity: apiKey } = useSelector(state => state.apiKey)

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo } = userLogin

	const locationId = location._id

	useEffect(() => {
		dispatch(initializeApiKey('REACT_APP_GOOGLE_API_KEY'))
	}, [location, locationId, dispatch])

	const handleEditLocation = async values => {
		const newLocation = await prepareLocationForSave(values)
		await dispatch(updateLocation(newLocation))
		await dispatch(initializeLocations())
	}

	const openEdit = location => {
		setRecordForEdit(location)
		setPopupTitle('Edit Location')
		setOpenPopup(true)
	}

	const openProjectEdit = project => {
		setRecordForEdit(project)
		setPopupTitle('Edit Project')
		setOpenModifyPopup(true)
	}

	const openInMap = location => {
		setCenter([location.latitude, location.longitude])
		setRecordForEdit(location)

		if (location.retailer) {
			setMapTitle(
				`${location.retailer}` +
					` - ` +
					`${location.assetName}` +
					` - ` +
					`${location.address}` +
					`, ` +
					`${location.city}` +
					`, ` +
					`${location.stateProvince}`,
			)
		}

		setMapTitle(
			`${location.assetName}` +
				` - ` +
				`${location.address}` +
				`, ` +
				`${location.city}` +
				`, ` +
				`${location.stateProvince}`,
		)

		setOpenMap(true)
	}

	const handleEditProject = async newProject => {
		await dispatch(
			patchProject({
				projectId: newProject._id,
				updates: {
					projectName: newProject.projectName,
					projectDescription: newProject.projectDescription,
					projectType: newProject.projectType,
					isActive: newProject.isActive,
					image: location.image,
					updateNote: 'Project edited.',
				},
			}),
		)
		dispatch(
			initializeProjects({
				locationId: locationId,
			}),
		)

		setOpenModifyPopup(false)
		dispatch(patchLocation({ locationId: locationId, updates: { updateNote: 'Project Edited' } }))
	}

	return (
		<div>
			<Layout.SidebarAssetTop
				image={location.image}
				assetName={
					location.owner._id === userInfo._id ? (
						<div
							className="font-semibold hover:no-underline hover:scale-110 cursor-pointer transition-transform duration-150 "
							onClick={() => openEdit(location)}
						>
							{location.assetName}{' '}
						</div>
					) : (
						<div className="text-lg font-semibold ">{location.assetName} </div>
					)
				}
				address={
					<div
						className="pb-2 hover:no-underline hover:scale-110 cursor-pointer transition-transform duration-150 "
						onClick={() => openInMap(location)}
					>
						<Link
							className="text-[#0080fe] hover:no-underline  "
							onClick={() => openInMap(location)}
							to="#"
						>
							{location.address} {location.city}, {location.stateProvince}
						</Link>
					</div>
				}
				webAddress={
					location.assetLink && (
						<span>
							<a target="_blank" rel="noopener noreferrer" href={location.assetLink}>
								{location.assetLink}
							</a>
						</span>
					)
				}
				originator={
					<div>
						<div className="pt-2">
							{originator}
							{location.owner.name}
						</div>
					</div>
				}
				project={
					project ? (
						<span onClick={() => openProjectEdit(project)}>
							{project.projectName}{' '}
							<button type="button" title="Edit" className="inline-flex items-center justify-center w-6 h-6 rounded text-[#0080fe] hover:bg-gray-100">
								<Pencil className="w-3.5 h-3.5" />
							</button>
						</span>
					) : null
				}
			/>
			{openMap ? (
				<MapLocationPopupCN
					open={openMap}
					onClose={() => setOpenMap(false)}
					apiKey={apiKey?.key}
					location={recordForEdit}
				/>
			) : null}
			<LocationActionSheet
				location={recordForEdit || location}
				open={openPopup}
				onClose={() => setOpenPopup(false)}
				onEditLocation={handleEditLocation}
				showActions={false}
			/>
			{openModifyPopup ? (
				<Popup title={popupTitle} openPopup={openModifyPopup} setOpenPopup={setOpenModifyPopup} icon={<FolderKanban className="w-5 h-5" />} description="Modify project details">
					<ProjectModifyPopup editProject={handleEditProject} recordForEdit={recordForEdit} />
				</Popup>
			) : null}
		</div>
	)
}

export default SidebarLocationTop
